import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { PlanPageStore } from 'src/pages/plan-page/plan-page.store';
import UploadIcon from 'src/shared/assets/icons/cloud.svg';
import CloseIcon from 'src/shared/assets/icons/cross.svg';
import { Button } from 'src/shared/components/button/button';
import { useStore } from 'src/store';
import { PlanVersionType } from 'src/store/comparison/types';
import {
  GOPLAN_DRILLING_CARPET_PUBLISH_PUBLIC_DRAFT,
  GOPLAN_DRILLING_CARPET_PUBLISH_PERSONAL_DRAFT,
} from 'src/store/roles-store/roles';

import styles from './compare-mode-buttons.module.scss';

interface Props {
  pageStore: PlanPageStore;
}

export const CompareModeButtons = observer(function CompareModeButtons({ pageStore }: Props) {
  const { t } = useTranslation();

  const { rolesStore, planVersionsService } = useStore();

  const isPublishingAvailable = useMemo(() => {
    const draft = planVersionsService.currentDraft;

    if (!draft) {
      return false;
    }

    const parentVersion = planVersionsService.versionsList.find((ver) => ver.id === draft.data.parentVersionId);

    if (!parentVersion) {
      return false;
    }

    if (parentVersion.data.versionType === PlanVersionType.public) {
      return rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_PUBLISH_PUBLIC_DRAFT]);
    }

    if (parentVersion.data.versionType === PlanVersionType.personal) {
      return rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_PUBLISH_PERSONAL_DRAFT]);
    }

    return false;
  }, [planVersionsService.currentDraft, planVersionsService.versionsList, rolesStore]);

  return (
    <div className={styles.container}>
      {pageStore.comparison.isImportOccurred ? (
        <div className={styles.importOccurredButtons}>
          {isPublishingAvailable && (
            <Button
              disabled={pageStore.comparison.isLoading}
              className={styles.button}
              variant="primary"
              icon={<UploadIcon />}
              onClick={pageStore.comparison.publishImportVersion}
            >
              {t('common:Buttons.toPublish')}
            </Button>
          )}
          <Button
            disabled={pageStore.comparison.isLoading}
            className={styles.button}
            variant="danger"
            icon={<CloseIcon />}
            onClick={pageStore.comparison.cancelImport}
          >
            {t('common:Buttons.cancel')}
          </Button>
        </div>
      ) : (
        <Button
          className={styles.button}
          variant="danger"
          icon={<CloseIcon />}
          onClick={pageStore.comparison.completeComparing}
        >
          {t('common:Buttons.finishComparison')}
        </Button>
      )}
    </div>
  );
});
